import React, { useCallback, useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import he from 'he';
import { v4 as UUID } from 'uuid';

import { Checkbox, GhostInput, PrimaryButton, Sub1Button } from '@components/atoms';
import { dialogContentsState } from '@components/templates';
import { ExpandRecipient, IRecipient } from '@models/strokeSetting/notification';
import {
  StyledBtnAddInfo,
  StyledButtonGroup,
  StyledRecipientsBox,
  StyledTable,
  StyledTelBox,
  StyledRecipients,
} from './styles';

interface Props {
  recipients: ExpandRecipient[];
  countryCallingCode?: string | null;
  onUpdateRecipients: (recipients: ExpandRecipient[]) => void;
}

const Recipient = ({ recipients = [], countryCallingCode, onUpdateRecipients }: Props) => {
  const [holeChecked, setHoleChecked] = useState(false);
  const setDialogContents = useSetRecoilState(dialogContentsState);

  const isComplete = useCallback((item: IRecipient | ExpandRecipient) => {
    const { name, countryCallingCode, tel, email } = item;
    if (!name) return false;
    const isEmptyCode = countryCallingCode === '';
    const isEmptyTel = tel === '';
    const hasTelAndCodeNum = !isEmptyCode && !isEmptyTel;
    const hasTelOrCodeOnly = (isEmptyCode && !isEmptyTel) || (!isEmptyCode && isEmptyTel);

    if ((!email && !hasTelAndCodeNum) || (!!email && hasTelOrCodeOnly)) return false;
    return true;
  }, []);

  /**
   * add, change, delete 후에 반드시 동작해야하는 공통 함수
   */
  const afterUpdateHandler = (newList: ExpandRecipient[]) => {
    onUpdateRecipients(newList);
  };

  /**
   * item 찾아서 복사본 return
   */
  const findAndCopy = (tempId: string, props: ExpandRecipient[]) => {
    const copy = [...props];
    const index = copy.findIndex((v) => v.tempId === tempId);
    if (index === -1) return null;
    return { copy, index };
  };

  /**
   * checked 핸들링
   */
  const checkedHandler = (tempId: string, checked: boolean) => {
    const _ = findAndCopy(tempId, recipients);
    if (!_) return;

    _.copy[_.index] = { ..._.copy[_.index], active: checked };

    const checkedSize = _.copy.filter(({ active }) => !!active).length;
    setHoleChecked(recipients.length === checkedSize);

    afterUpdateHandler(_.copy);
  };

  /**
   * 전체 check 핸들링
   */
  const holeCheckedHandler = (checked: boolean) => {
    setHoleChecked(checked);

    const copy = [...recipients];
    const next = copy.map((item) => ({ ...item, active: checked }));

    afterUpdateHandler(next);
  };

  /**
   * edit 활성/비봘성 핸들러
   */
  const editToggleHandler = (tempId: string) => {
    const _ = findAndCopy(tempId, recipients);
    if (!_) return;

    let code = '';
    const { isEdit, tel, countryCallingCode } = { ..._.copy[_.index] };

    if (!isEdit === false) {
      // 다음 상태가 edit 이 아닐때, tel이 없으면 빈값으로 read only 상태 전환, tel 이 있으면 국가코드 부여
      code = tel === '' ? '' : countryCallingCode || '';
    } else {
      // 다음 상태가 edit 이면, 무조건 국가코드 부여
      code = countryCallingCode || '';
    }

    _.copy[_.index] = {
      ..._.copy[_.index],
      isEdit: !isEdit,
      countryCallingCode: code,
    };

    afterUpdateHandler(_.copy);
  };

  /**
   * row 삭제 핸들러
   */
  const deleteHandler = (tempId: string) => {
    const copy = [...recipients];

    const item = copy.find((v) => v.tempId === tempId);

    if ((item && item.id) || (item && (item.email || item.tel) && item.name)) {
      setDialogContents({
        type: 'plain',
        body: `Do you want delete it?`,
        footer: (
          <>
            <Sub1Button
              onClick={() => {
                setDialogContents({ type: 'hidden' });
              }}
            >
              Cancel
            </Sub1Button>
            <PrimaryButton
              onClick={() => {
                const next = copy.filter((v) => v.tempId !== tempId);
                afterUpdateHandler(next);
                setDialogContents({ type: 'hidden' });
              }}
            >
              Delete
            </PrimaryButton>
          </>
        ),
      });
      return;
    }

    const next = copy.filter((v) => v.tempId !== tempId);
    const checkedSize = next.filter(({ active }) => !!active).length;
    setHoleChecked(next.length > 0 && next.length === checkedSize);

    afterUpdateHandler(next);
  };

  /**
   * 값 변경 핸들러
   */
  const changeHandler = (tempId: string, ev: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = ev.target;

    // 이름 유효성
    // 1~30자 까지 입력 가능하며, 30자 이상 입력 불가
    if (name === 'name' && value.length > 30) return;

    // 전화번호 유효성
    // 숫자만 입력 가능하며 숫자외의 문자 입력 시 짧은 지연시간 후 자동 삭제
    if (name === 'tel' || name === 'countryCallingCode') {
      if (!/^[0-9]+$/.test(value) && value !== '') return;
    }

    const _ = findAndCopy(tempId, recipients);
    if (!_) return;

    _.copy[_.index][name as 'name' | 'email' | 'tel' | 'countryCallingCode'] = value;
    _.copy[_.index].isComplete = isComplete(_.copy[_.index]);

    afterUpdateHandler(_.copy);
  };

  /**
   *  Designator, on call 값 변경 핸들러
   */
  const changeBooleanHandler = (tempId: string, ev: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = ev.target;
    const _ = findAndCopy(tempId, recipients);
    if (!_) return;

    _.copy[_.index][name as 'designator' | 'oncall'] = checked;
    afterUpdateHandler(_.copy);
  };

  /**
   * Recipient row 추가
   */
  const rowAddHandler = () => {
    const next = [
      ...recipients,
      {
        active: false,
        isEdit: true,
        isComplete: false,
        countryCallingCode: countryCallingCode || '',
        email: '',
        name: '',
        tel: '',
        id: null,
        notificationId: 0,
        designator: false,
        oncall: false,
        tempId: UUID(),
      },
    ];

    const checkedSize = next.filter(({ active }) => !!active).length;
    setHoleChecked(next.length === checkedSize);
    afterUpdateHandler(next);
  };

  /**
   * activeAll 초기값 넣어주기
   */
  useEffect(() => {
    const activeTrueLength = recipients.filter((item, i) => !!item.active).length;

    setHoleChecked(recipients.length > 0 && recipients.length === activeTrueLength);
  }, []);

  return (
    <StyledRecipientsBox>
      <StyledRecipients>
        <StyledTable>
          <table>
            <thead>
              <tr>
                <th>
                  <span className={'active-text'}>Active</span>
                  <Checkbox
                    className={'all-check'}
                    checked={holeChecked}
                    onChange={(ev) => {
                      holeCheckedHandler(ev.target.checked);
                    }}
                  />
                </th>
                <th>Name</th>
                <th>E-mail</th>
                <th>Tel</th>
                <th>
                  <div className="tooltip">
                    <span>Designator</span>
                    <div className="tooltip-wrapper">
                      <div className="tooltip-content">
                        <div>Send a notification to a user whose designator is checked</div>
                        <div>if condition sending is enabled but there is no metatag value</div>
                      </div>
                    </div>
                  </div>
                </th>
                <th>
                  <div className="tooltip">
                    <span>On-Call</span>
                    <div className="tooltip-wrapper">
                      <div className="tooltip-content">
                        <div>Always send regardless of Notification settings</div>
                        <div>Note: This applies to cases where notifications are always required,</div>
                        <div>such as duty phones.</div>
                      </div>
                    </div>
                  </div>
                </th>
                <th>Modify</th>
              </tr>
            </thead>
            <tbody>
              {recipients.map(
                ({ isEdit, isComplete, active, email, name, tel, countryCallingCode, tempId, designator, oncall }) => {
                  return (
                    <tr key={tempId}>
                      <td>
                        <Checkbox checked={active} onChange={({ target }) => checkedHandler(tempId, target.checked)} />
                      </td>
                      <td>
                        <GhostInput
                          name="name"
                          textAlign="center"
                          placeholder="Name(Require value)"
                          readOnly={!isEdit}
                          value={he.decode(name || '')}
                          onChange={(ev) => changeHandler(tempId, ev)}
                        />
                      </td>
                      <td>
                        <GhostInput
                          name="email"
                          textAlign="center"
                          placeholder="E-mail"
                          readOnly={!isEdit}
                          value={he.decode(email)}
                          onChange={(ev) => changeHandler(tempId, ev)}
                        />
                      </td>
                      <td>
                        <StyledTelBox>
                          {isEdit || (tel && countryCallingCode) ? <span>+</span> : null}
                          <GhostInput
                            name="countryCallingCode"
                            style={{ width: isEdit ? 36 : 26, padding: 0 }}
                            textAlign="center"
                            value={countryCallingCode}
                            readOnly={!isEdit}
                            onChange={(ev) => changeHandler(tempId, ev)}
                          />
                          <GhostInput
                            name="tel"
                            style={{ width: isEdit ? 130 : 86, padding: 0 }}
                            textAlign="center"
                            placeholder="Tel(Only number)"
                            readOnly={!isEdit}
                            value={tel}
                            onChange={(ev) => changeHandler(tempId, ev)}
                          />
                        </StyledTelBox>
                      </td>
                      <td>
                        <Checkbox
                          name="designator"
                          className={'designator'}
                          checked={designator}
                          onChange={(ev) => {
                            changeBooleanHandler(tempId, ev);
                          }}
                        />
                      </td>
                      <td>
                        <Checkbox
                          name="oncall"
                          className={'oncall'}
                          checked={oncall}
                          onChange={(ev) => {
                            changeBooleanHandler(tempId, ev);
                          }}
                        />
                      </td>
                      <td>
                        {isEdit ? (
                          <StyledButtonGroup>
                            <button
                              className={isComplete ? 'btn-add_on' : 'btn-add_off'}
                              onClick={() => {
                                if (isComplete) {
                                  editToggleHandler(tempId);
                                }
                              }}
                            />
                            <button className="btn-delete" onClick={() => deleteHandler(tempId)} />
                          </StyledButtonGroup>
                        ) : (
                          <StyledButtonGroup>
                            <button className="btn-modify" onClick={() => editToggleHandler(tempId)} />
                            <button className="btn-delete" onClick={() => deleteHandler(tempId)} />
                          </StyledButtonGroup>
                        )}
                      </td>
                    </tr>
                  );
                },
              )}
            </tbody>
          </table>
        </StyledTable>
        <StyledBtnAddInfo onClick={rowAddHandler}>
          <span>Add information</span>
        </StyledBtnAddInfo>
      </StyledRecipients>
      <p className="txt-total_count">Total {recipients.length}</p>
    </StyledRecipientsBox>
  );
};

export default Recipient;
